import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import DialogComponent from "../../../../../../common/components/functional/dialogs/DialogComponent";
import { ModalState } from "../../../../types/types";
import {
  PRICING_SPECIAL_PATH_START,
  PRICING_SPECIAL_SETTING_KEYS,
  PricingSpecialSetting,
} from "../../../constants";
import LabelSelectRow from "../../../form/LabelSelectRow";
import { PricingGuideText, StyledFormSection } from "../../../styles";
import BaseHoursAndSupplementsRulesAndPricingForm from "./BaseHoursAndSupplementsRulesAndPricingForm";
import EarlyBirdOrderRulesAndPricingForm from "./EarlyBirdOrderRulesAndPricingForm";
import ExpressOrderRulesAndPricingForm from "./ExpressOrderRulesAndPricingForm";
import { CustomerRecruitPricing } from "./CustomerRecruitPricing";

enum Enabled {
  YES = "yes",
  NO = "no",
}

const OPTIONS = [Enabled.YES, Enabled.NO];

interface isValidProps {
  enabled: Enabled;
  settings?: any;
}

interface Props {
  disabled?: boolean;
}

const PATH_START = PRICING_SPECIAL_PATH_START;
const DRAG_HANDLE_SELECTOR = "drag-handle-pricing-special";

const PricingSpecialSection = ({ disabled }: Props) => {
  const { t } = useTranslation("customers");
  const {
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    id: "",
  });

  const onOpenModal = (id?: string) => setModalState({ show: true, id });
  const onCloseModal = () =>
    setModalState((prevState) => ({ ...prevState, show: false }));
  const onModalExited = () => {
    setModalState({ show: false, id: "" });
  };

  const tesId = getValues("tesId");

  const getObjectPathForKey = (key: PricingSpecialSetting) =>
    `${PATH_START}.${key}`;

  const getModalContent = () => {
    switch (modalState.id) {
      case PricingSpecialSetting.ExpressOrder:
        return (
          <ExpressOrderRulesAndPricingForm
            disabled={disabled}
            onClose={onCloseModal}
            canEdit={true}
            tesId={tesId}
          />
        );
      case PricingSpecialSetting.OfficeOrder:
        return (
          <BaseHoursAndSupplementsRulesAndPricingForm
            title={t("contract.officeOrderRulesAndPricing")}
            bodyText={t("contract.officeOrderGuideText")}
            formDataPathKey={getObjectPathForKey(
              PricingSpecialSetting.OfficeOrder,
            )}
            disabled={disabled}
            onClose={onCloseModal}
            tesId={tesId}
          />
        );
      case PricingSpecialSetting.EarlyBirdOrder:
        return (
          <EarlyBirdOrderRulesAndPricingForm
            disabled={disabled}
            onClose={onCloseModal}
            tesId={tesId}
          />
        );
      case PricingSpecialSetting.LongOrder:
        return (
          <BaseHoursAndSupplementsRulesAndPricingForm
            title={t("contract.longOrderRulesAndPricing")}
            bodyText={t("contract.longOrderGuideText")}
            formDataPathKey={getObjectPathForKey(
              PricingSpecialSetting.LongOrder,
            )}
            disabled={disabled}
            onClose={onCloseModal}
            tesId={tesId}
          />
        );
      case PricingSpecialSetting.CustomerRecruit:
        return (
          <CustomerRecruitPricing
            title={t("contract.customerRecruitRulesAndPricing")}
            formDataPathKey={getObjectPathForKey(
              PricingSpecialSetting.CustomerRecruit,
            )}
            disabled={disabled}
            canEdit={!disabled}
            onClose={onCloseModal}
            tesId={tesId}
          />
        );
      default:
        return <></>;
    }
  };

  const getFieldNamePath = (key: PricingSpecialSetting) =>
    `${getObjectPathForKey(key)}.enabled`;

  const isValid = (prop: isValidProps): boolean =>
    prop.enabled === Enabled.NO ||
    (prop.enabled === Enabled.YES && !!prop.settings);

  const handleResetProperty = (newValue: Enabled, propertyPath: string) => {
    if (newValue === Enabled.NO) {
      const resetValue = { enabled: Enabled.NO, settings: null };
      setValue(propertyPath, resetValue, { shouldDirty: true });
    }
  };

  const getDefaultRowProps = (
    pricingSpecialSettingKey: PricingSpecialSetting,
  ) => {
    const specialSettingObjectPath = getObjectPathForKey(
      pricingSpecialSettingKey,
    );
    const specialSettingValues = watch(specialSettingObjectPath);
    return {
      fieldName: getFieldNamePath(pricingSpecialSettingKey),
      onSelectChange: (newValue: Enabled) =>
        handleResetProperty(newValue, specialSettingObjectPath),
      options: OPTIONS,
      getDisplayValue: (val: any) => t(`common:${val}`),
      selectWidth: 2,
      showButton: specialSettingValues.enabled === Enabled.YES,
      buttonError: !!errors[pricingSpecialSettingKey],
      showViewButton: !!specialSettingValues.settings,
      viewButtonText: t("contract.rulesAndPricing"),
      editButtonText: t("contract.rulesAndPricing"),
      onButtonClick: () => onOpenModal(pricingSpecialSettingKey),
      disabled: disabled,
    };
  };

  const watchedSpecials = watch(`${PATH_START}`);

  // TODO this works, but is there a better way to handle validation?
  // Validate values
  useEffect(() => {
    const specialSettingKeys = PRICING_SPECIAL_SETTING_KEYS;
    specialSettingKeys.forEach((key) => {
      const setting = getValues(getObjectPathForKey(key));
      if (!isValid(setting)) {
        setError(key, {});
      } else {
        clearErrors(key);
      }
    });
  }, [
    setError,
    clearErrors,
    watchedSpecials.expressOrder.enabled,
    watchedSpecials.expressOrder.settings,
    watchedSpecials.officeOrder.enabled,
    watchedSpecials.officeOrder.settings,
    watchedSpecials.earlyBirdOrder.enabled,
    watchedSpecials.earlyBirdOrder.settings,
    watchedSpecials.longOrder.enabled,
    watchedSpecials.longOrder.settings,
    watchedSpecials.customerRecruit.enabled,
    watchedSpecials.customerRecruit.settings,
    getValues,
  ]);

  return (
    <StyledFormSection title={t("contract.specialPricing")}>
      <PricingGuideText>
        {t("contract.pricingSpecialGuideText")}
      </PricingGuideText>
      <LabelSelectRow
        label={t("contract.customerRecruitPricing")}
        {...getDefaultRowProps(PricingSpecialSetting.CustomerRecruit)}
      />
      <LabelSelectRow
        label={t("contract.expressOrderPricing")}
        {...getDefaultRowProps(PricingSpecialSetting.ExpressOrder)}
      />
      <LabelSelectRow
        label={t("contract.earlyBirdPricing")}
        {...getDefaultRowProps(PricingSpecialSetting.EarlyBirdOrder)}
      />
      <LabelSelectRow
        label={t("contract.longOrderPricing")}
        {...getDefaultRowProps(PricingSpecialSetting.LongOrder)}
      />
      <LabelSelectRow
        label={t("contract.officeOrderPricing")}
        {...getDefaultRowProps(PricingSpecialSetting.OfficeOrder)}
      />
      <DialogComponent
        open={modalState.show}
        onClose={onCloseModal}
        sx={{ width: "694px" }}
        dragHandleSelector={DRAG_HANDLE_SELECTOR}
        TransitionProps={{ onExited: onModalExited }}
      >
        {getModalContent()}
      </DialogComponent>
    </StyledFormSection>
  );
};

export default PricingSpecialSection;
